import type { AnySceneObjectT } from "tweenly"
import { ObjectStyles, getObjectPositioning } from "./object.helpers"

/**
 * Pure geometry helpers shared by the multiselect wrapper, the single-object wrapper
 * and the grouping action. No React / Redux / DOM in this module — `objectsById` and
 * `getSize` are injected so the core stays synchronous and unit-testable.
 */

export type ObjectStylesWithId = ObjectStyles & {
    objectId: string
}

export const DEFAULT_OBJECT_STYLES: ObjectStyles = { top: 0, left: 0, width: 0, height: 0 }
export const DEFAULT_OBJECT_STYLES_WITH_ID: ObjectStylesWithId = {
    ...DEFAULT_OBJECT_STYLES,
    objectId: "",
}

export type Pos = { top: number; left: number }
export type Size = { width: number; height: number }
/**
 * How big an object is drawn, and how thick its top/left border is.
 *
 * The size is the rendered (border-box) one: a content-box object with a border or padding
 * is drawn larger than its width/height styles say. The border matters for its CHILDREN —
 * an absolutely positioned child is placed inside the parent's border, not at its edge.
 */
export type ObjectMeasure = Size & { borderTop: number; borderLeft: number }
export type ObjectsById = Record<string, AnySceneObjectT>
export type SizeFn = (object: AnySceneObjectT) => ObjectMeasure

export type WrapperBounds = {
    origin: Pos
    width: number
    height: number
    objectsAbsolute: ObjectStylesWithId[]
    childIds: string[]
}

/**
 * Absolute canvas position of an object = the sum of its own and all of its ancestors'
 * top/left, plus every ancestor's border (a child is positioned inside it). Walks the parent
 * chain via `objectsById` (no recursion limit issues, no shared-array accumulator).
 * Non-numeric values fall back to 0 (never NaN).
 *
 * @param getSize injectable measure, only its borders are used (defaults to none)
 */
export const getAbsoluteObjectPosition = (
    objectId: string,
    objectsById: ObjectsById,
    getSize: SizeFn = numericObjectSize
): Pos => {
    let top = 0
    let left = 0
    let current: AnySceneObjectT | undefined = objectsById[objectId]
    while (current) {
        const styles = getObjectPositioning(current)
        top += Number(styles.top) || 0
        left += Number(styles.left) || 0
        const parent: AnySceneObjectT | undefined = current.parentId
            ? objectsById[current.parentId]
            : undefined
        if (parent) {
            const { borderTop, borderLeft } = getSize(parent)
            top += borderTop
            left += borderLeft
        }
        current = parent
    }
    return { top, left }
}

/**
 * Collect an object's id and the ids of all of its descendants (depth-first).
 */
export const collectObjectIds = (
    objectId: string,
    objectsById: ObjectsById,
    acc: string[] = []
): string[] => {
    const object = objectsById[objectId]
    if (!object) return acc
    acc.push(object.id)
    object.childIds.forEach((childId) => {
        if (objectsById[childId]) collectObjectIds(childId, objectsById, acc)
    })
    return acc
}

/**
 * Of the currently selected ids, return those whose ancestor is ALSO selected.
 * These should be dropped from the selection (a parent already represents them).
 * Pure data — the caller decides what to do with it.
 */
export const getDescendantsOfSelected = (
    selectedIds: string[],
    objectsById: ObjectsById
): string[] => {
    const selectedSet = new Set(selectedIds)
    return selectedIds.filter((id) => {
        let parentId = objectsById[id]?.parentId ?? null
        while (parentId) {
            if (selectedSet.has(parentId)) return true
            parentId = objectsById[parentId]?.parentId ?? null
        }
        return false
    })
}

/**
 * Numeric size from the object's styles. `auto` (e.g. text) collapses to 0 so it can
 * never produce NaN, and borders/padding are unknown, so they count as none. Replace with
 * `domObjectSize` when the real rendered geometry is needed (see the hook).
 */
export const numericObjectSize: SizeFn = (object) => {
    const styles = getObjectPositioning(object)
    return {
        width: styles.width === "auto" ? 0 : Number(styles.width) || 0,
        height: styles.height === "auto" ? 0 : Number(styles.height) || 0,
        borderTop: 0,
        borderLeft: 0,
    }
}

/** A computed CSS length in px, 0 when it is not one. */
const px = (value: string): number => parseFloat(value) || 0

/**
 * DOM-backed measure of the rendered `#objectId` element.
 *
 * The size is the stored width/height plus the border and padding a content-box object is
 * drawn with — not `offsetWidth`, which would also pick up a width the timeline is animating
 * at the moment. Only `auto` dimensions (text) and missing ones (a line's height) are
 * measured outright. The borders come from
 * the computed style, so a `border-style: none` border counts as 0, like the browser draws
 * it. Falls back to the numeric styles when the element is not rendered (never NaN). Use
 * this in hook/render code; `numericObjectSize` is the pure variant for non-DOM
 * contexts/tests.
 */
export const domObjectSize: SizeFn = (object) => {
    const element = typeof document !== "undefined" ? document.getElementById(object.id) : null
    if (!element) return numericObjectSize(object)

    const styles = getObjectPositioning(object)
    const computed = getComputedStyle(element)
    const borderTop = px(computed.borderTopWidth)
    const borderLeft = px(computed.borderLeftWidth)
    const isContentBox = computed.boxSizing !== "border-box"
    const extraWidth = isContentBox
        ? borderLeft +
          px(computed.borderRightWidth) +
          px(computed.paddingLeft) +
          px(computed.paddingRight)
        : 0
    const extraHeight = isContentBox
        ? borderTop +
          px(computed.borderBottomWidth) +
          px(computed.paddingTop) +
          px(computed.paddingBottom)
        : 0

    // `auto` sizes itself (text); a missing style is set by something else (a line's height
    // is its weight) — both are measured
    const isMeasured = (value: unknown) => value === "auto" || value === undefined
    return {
        width: isMeasured(styles.width)
            ? element.offsetWidth
            : (Number(styles.width) || 0) + extraWidth,
        height: isMeasured(styles.height)
            ? element.offsetHeight
            : (Number(styles.height) || 0) + extraHeight,
        borderTop,
        borderLeft,
    }
}

/**
 * Bounding box over `rootIds` and all of their descendants, in absolute canvas
 * coordinates. Returns the origin (top-left), the box size, the absolute position of
 * every object involved, and the flat list of all collected ids.
 *
 * @param getSize injectable size resolver (defaults to the pure numeric size)
 */
export const computeWrapperBounds = (
    rootIds: string[],
    objectsById: ObjectsById,
    getSize: SizeFn = numericObjectSize
): WrapperBounds => {
    const tops: number[] = []
    const lefts: number[] = []
    const rights: number[] = []
    const bottoms: number[] = []
    const objectsAbsolute: ObjectStylesWithId[] = []
    const childIds: string[] = []

    rootIds.forEach((rootId) => {
        collectObjectIds(rootId, objectsById).forEach((id) => {
            const object = objectsById[id]
            if (!object) return
            childIds.push(id)

            const { top, left } = getAbsoluteObjectPosition(id, objectsById, getSize)
            const { width, height } = getSize(object)
            const edgeRight = left + width
            const edgeBottom = top + height

            tops.push(top)
            lefts.push(left)
            rights.push(edgeRight)
            bottoms.push(edgeBottom)
            objectsAbsolute.push({
                top,
                left,
                width: edgeRight - left,
                height: edgeBottom - top,
                objectId: id,
            })
        })
    })

    if (!tops.length) {
        return {
            origin: { top: 0, left: 0 },
            width: 0,
            height: 0,
            objectsAbsolute: [],
            childIds: [],
        }
    }

    const minTop = Math.min(...tops)
    const minLeft = Math.min(...lefts)
    return {
        origin: { top: minTop, left: minLeft },
        width: Math.max(...rights) - minLeft,
        height: Math.max(...bottoms) - minTop,
        objectsAbsolute,
        childIds,
    }
}

/**
 * Wrapper geometry for a SINGLE selected object, in that object's parent coordinate space.
 * Shared by `useWrapSelectedObject` and the grouping thunk so the two can never drift apart.
 */
export const singleSelectionWrapper = (
    selectedId: string,
    objectsById: ObjectsById,
    getSize: SizeFn = domObjectSize
): { wrapperObjectsStyles: ObjectStylesWithId; activeChildObjectIds: string[] } => {
    const selectedObject = selectedId ? objectsById[selectedId] : undefined
    if (!selectedObject) {
        return { wrapperObjectsStyles: DEFAULT_OBJECT_STYLES_WITH_ID, activeChildObjectIds: [] }
    }

    const bounds = computeWrapperBounds([selectedId], objectsById, getSize)
    const absSelected = getAbsoluteObjectPosition(selectedId, objectsById, getSize)
    const localStyles = getObjectPositioning(selectedObject)
    const relativeTop = absSelected.top - (Number(localStyles.top) || 0)
    const relativeLeft = absSelected.left - (Number(localStyles.left) || 0)

    return {
        wrapperObjectsStyles: {
            top: bounds.origin.top - relativeTop,
            left: bounds.origin.left - relativeLeft,
            width: bounds.width,
            height: bounds.height,
            objectId: selectedId,
        },
        activeChildObjectIds: bounds.childIds,
    }
}

/**
 * Wrapper geometry for a MULTI selection, in absolute canvas coordinates.
 */
export const multiSelectionWrapper = (
    selectedIds: string[],
    objectsById: ObjectsById,
    getSize: SizeFn = domObjectSize
): {
    wrapperObject: ObjectStyles
    allObjectsInWrapper: ObjectStylesWithId[]
    activeChildObjectsIds: string[]
} => {
    const bounds = computeWrapperBounds(selectedIds, objectsById, getSize)
    return {
        wrapperObject: {
            top: bounds.origin.top,
            left: bounds.origin.left,
            width: bounds.width,
            height: bounds.height,
        },
        allObjectsInWrapper: bounds.objectsAbsolute,
        activeChildObjectsIds: bounds.childIds,
    }
}
